import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toLocaleCurrency } from 'utils/dinero'
import { trackEvent } from 'analytics/trackFunctions'

import RoundedIcon from 'components/RoundedIcon'
import ClientInDebtModal from 'components/LineModals/ClientInDebtModal'

import { Retry } from 'assets/icons'
import { URL_RENOVADOR } from 'config'

// calls
import { CREDIT_PRODUCT, getOffer } from 'api/advances'
import { UserContext } from 'contexts/user'
import { OFFER_ERROR_SLUGS } from 'constants/credit'

const { WALLET, LOANS } = CREDIT_PRODUCT
const { inArrears, badScore, lineIsDepleted, noProposalAvailable } =
  OFFER_ERROR_SLUGS

const buttonClasses =
  'text-2xs leading-[14px] text-gray-900 font-medium px-1 py-3 rounded-md shadow-card'

const Buttons = ({
  advanceSummary,
  loanSummary,
  setAdvanceSummary,
  setLoanSummary
}) => {
  const [isGettingAdvanceOffer, setIsGettingAdvanceOffer] =
    useState<boolean>(false)
  const [isGettingLoanOffer, setIsGettingLoanOffer] = useState<boolean>(false)
  const [debtModalIsOpen, setDebtModalIsOpen] = useState<boolean>(false)

  const { user } = useContext(UserContext)

  const navigate = useNavigate()

  function handleOfferQuery(product) {
    const isGettingOffer =
      product === WALLET ? isGettingAdvanceOffer : isGettingLoanOffer
    const setIsGettingOffer =
      product === WALLET ? setIsGettingAdvanceOffer : setIsGettingLoanOffer
    const setSummary = product === WALLET ? setAdvanceSummary : setLoanSummary

    if (isGettingOffer) return
    setIsGettingOffer(true)

    getOffer(product)
      .then((res) => {
        if (res.success) {
          const summary = res.data.result

          let newSummary: any = {
            remainingAmount:
              product === WALLET
                ? summary.cash_advances.max_amount
                : summary.personal_loans.max_amount,
            isValid: true
          }

          if (product === WALLET) {
            newSummary.interestFreeRemainingAmount =
              summary.interest_free_advances.max_amount
          }

          setSummary((prev) => ({
            ...prev,
            ...newSummary
          }))
        } else {
          const { code } = res.error

          if (
            code === badScore ||
            code === noProposalAvailable ||
            (code === lineIsDepleted && product === LOANS)
          ) {
            let newSummary: any = {
              remainingAmount: 0,
              allocatedAmount: 0,
              isValid: true,
              isZero: true
            }

            setSummary((prev) => ({
              ...prev,
              ...newSummary
            }))
          }

          if (code === inArrears) {
            setDebtModalIsOpen(true)
          }
        }
      })
      .finally(() => setIsGettingOffer(false))
  }

  const loanButton = () => {
    if (!loanSummary.isValid) {
      return (
        <button
          onClick={() => {
            trackEvent({ eventName: 'bille_boton_consultar_oferta_prestamos' })
            handleOfferQuery(LOANS)
          }}
          className={`${buttonClasses} flex justify-between items-center gap-2`}
        >
          <span className='w-full'>Consultar Préstamos</span>
          <RoundedIcon
            className={`${
              isGettingLoanOffer ? 'bg-purple-900/80' : 'bg-purple-900'
            } w-6 h-6`}
            icon={
              <Retry
                className={`${isGettingLoanOffer && 'animate-spin-slow'}`}
              />
            }
          />
        </button>
      )
    }

    if (loanSummary.isZero) {
      return (
        <div className={`${buttonClasses} text-center`}>
          No tenés línea de{' '}
          <span className='text-green-800 font-bold'>Préstamos</span> disponible
        </div>
      )
    }

    if (loanSummary.isDepleted) {
      return (
        <div className={`${buttonClasses} text-center`}>
          Agotaste tu línea de{' '}
          <span className='text-green-800 font-bold'>Préstamos</span>
        </div>
      )
    }

    return (
      <button
        onClick={() => {
          trackEvent({ eventName: 'bille_boton_prestamos' })
          window
            .open(
              `${URL_RENOVADOR}iniciando?access_token=${user.access_token}`,
              '_blank'
            )
            .focus()
        }}
        className={buttonClasses}
      >
        Hasta ${toLocaleCurrency(loanSummary.remainingAmount)} <br /> en{' '}
        <span className='text-green-800 font-bold'>Préstamos</span>
      </button>
    )
  }

  const advanceButtons = () => {
    if (!advanceSummary.isValid) {
      return (
        <button
          onClick={() => {
            trackEvent({ eventName: 'bille_boton_consultar_oferta_adelantos' })
            handleOfferQuery(WALLET)
          }}
          className={`${buttonClasses} flex justify-between items-center gap-2`}
        >
          <span className='w-full'>Consultar Adelantos y Tasa 0%</span>
          <RoundedIcon
            className={`${
              isGettingAdvanceOffer ? 'bg-purple-900/80' : 'bg-purple-900'
            } w-6 h-6`}
            icon={
              <Retry
                className={`${isGettingAdvanceOffer && 'animate-spin-slow'}`}
              />
            }
          />
        </button>
      )
    }

    const interestFreeButton = () => {
      if (advanceSummary.isZero) {
        return (
          <div className={`${buttonClasses} text-center`}>
            No tenés adelantos a{' '}
            <span className='text-blue-900 font-bold'>Tasa 0%</span> disponible
          </div>
        )
      }

      if (advanceSummary.interestFreeIsDepleted || advanceSummary.isDepleted) {
        return (
          <div className={`${buttonClasses} text-center`}>
            Consumiste tus adelantos a{' '}
            <span className='text-blue-900 font-bold'>Tasa 0%</span>
          </div>
        )
      }

      return (
        <button
          onClick={() => {
            trackEvent({ eventName: 'bille_boton_tasa_0' })
            navigate('/pedir-adelanto/sin-interes')
          }}
          className={buttonClasses}
        >
          Hasta ${toLocaleCurrency(advanceSummary.interestFreeRemainingAmount)}{' '}
          <br /> en <span className='text-blue-900 font-bold'>Tasa 0%</span>
        </button>
      )
    }

    const cashAdvanceButton = () => {
      if (advanceSummary.isZero) {
        return (
          <div className={`${buttonClasses} text-center`}>
            No tenés línea de{' '}
            <span className='text-purple-900 font-bold'>Adelantos</span>{' '}
            disponible
          </div>
        )
      }

      if (advanceSummary.isDepleted) {
        return (
          <div className={`${buttonClasses} text-center`}>
            Agotaste tu línea de{' '}
            <span className='text-purple-900 font-bold'>Adelantos</span>
          </div>
        )
      }

      return (
        <button
          onClick={() => {
            trackEvent({ eventName: 'bille_boton_adelantos' })
            navigate('/pedir-adelanto')
          }}
          className={buttonClasses}
        >
          Hasta ${toLocaleCurrency(advanceSummary.remainingAmount)} <br /> en{' '}
          <span className='text-purple-900 font-bold'>Adelantos</span>
        </button>
      )
    }

    return (
      <>
        {interestFreeButton()}
        {cashAdvanceButton()}
      </>
    )
  }

  return (
    <>
      <div
        className={`grid ${
          advanceSummary.isValid
            ? 'grid-cols-[1fr,_1fr,_1fr]'
            : 'grid-cols-[1fr,_1fr]'
        } gap-2`}
      >
        {advanceButtons()}
        {loanButton()}
      </div>
      <ClientInDebtModal
        isOpen={debtModalIsOpen}
        setIsOpen={setDebtModalIsOpen}
      />
    </>
  )
}

export default Buttons
